import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { MapPin, Zap } from "lucide-react";
import { api, errCode } from "../lib/api";
import { useLang } from "../lib/i18n";
import { useMaster } from "../lib/master";
import { todayCL } from "../lib/time";
import { Sel, Inp } from "../components/Common";
import { ReleaseBadge } from "../components/Badges";
import { ScanError } from "./Scan";
import { useAuth } from "../lib/auth";
import { isNetErr, findLabel, getBundle, getLocalBins, offlineRelease, saveLocalBin, enqueue, newId } from "../lib/offline";

const getGPS = () => new Promise((res, rej) => {
  if (!navigator.geolocation) return rej(new Error("no_geo"));
  navigator.geolocation.getCurrentPosition(
    (p) => res({ lat: p.coords.latitude, lng: p.coords.longitude, accuracy: Math.round(p.coords.accuracy), captured_at: new Date(p.timestamp).toISOString() }),
    rej, { enableHighAccuracy: true, maximumAge: 0, timeout: 15000 });
});

export default function Activate() {
  const { number } = useParams();
  const { t, te } = useLang();
  const nav = useNavigate();
  const [label, setLabel] = useState(null);
  const [err, setErr] = useState("");
  const [asg, setAsg] = useState(null);
  const contractors = useMaster("contractors");
  const crews = useMaster("crews");
  const varieties = useMaster("varieties");
  const [f, setF] = useState({ contractor_id: "", crew_id: "", sector_id: "", variety_id: "", harvest_date: todayCL(), notes: "" });
  const [rel, setRel] = useState(null);
  const [gpsState, setGpsState] = useState("idle");
  const [noGps, setNoGps] = useState("");
  const [busy, setBusy] = useState(false);
  const [clientId] = useState(newId);
  const { user } = useAuth();

  useEffect(() => {
    api.get(`/scan/${number}`).then(({ data }) => {
      if (data.state === "active") return nav(`/bin/${data.bin_id}`, { replace: true });
      setLabel(data.label);
    }).catch(async (e) => {
      if (!isNetErr(e)) return setErr(errCode(e));
      const lb = await findLabel(number);
      if (!lb) return setErr("offline_label_unknown");
      const local = Object.values(await getLocalBins()).find((b) => b.label_id === lb.id);
      if (local || lb.bin_id) return nav(`/bin/${local?.id || lb.bin_id}`, { replace: true });
      setLabel(lb);
    });
    const applyAsg = (data) => {
      setAsg(data);
      const last = data.last_activation || JSON.parse(localStorage.getItem("hf_last_activation") || "null");
      if (last) setF((p) => ({ ...p, ...Object.fromEntries(Object.entries(last).filter(([, v]) => v)) }));
    };
    api.get("/me/assignments").then(({ data }) => applyAsg(data))
      .catch(async () => { const b = await getBundle(); if (b) applyAsg({ ...b, last_activation: null }); });
  }, [number, nav]);

  useEffect(() => {
    if (!f.sector_id || !f.variety_id || !f.harvest_date) return setRel(null);
    api.get("/releases/check", { params: { sector_id: f.sector_id, variety_id: f.variety_id, date: f.harvest_date } })
      .then((r) => setRel(r.data))
      .catch(async (e) => setRel(isNetErr(e) ? await offlineRelease(f.sector_id, f.variety_id, f.harvest_date) : null));
  }, [f.sector_id, f.variety_id, f.harvest_date]);

  if (err && !label) return <div className="max-w-md mx-auto"><ScanError err={err} /></div>;
  if (!label || !asg) return <p>{t("loading")}</p>;

  const sectors = asg.sectors.filter((s) => s.farm_id === label.farm_id).map((s) => ({ ...s, name: `${s.name} (${s.code})` }));
  const myContractors = contractors.filter((c) => c.grower_id === label.grower_id).map((c) => ({ ...c, name: `${c.code} · ${c.name}` }));
  const myCrews = crews.filter((c) => c.contractor_id === f.contractor_id && c.farm_id === label.farm_id);
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v, ...(k === "contractor_id" ? { crew_id: "" } : {}) }));
  const released = rel?.status === "released";
  const ok = released && f.contractor_id && f.sector_id && f.variety_id;

  const saveOffline = async (payload) => {
    const now = new Date().toISOString();
    const nm = (list, id) => list.find((x) => x.id === id)?.name;
    await saveLocalBin({ id: clientId, label_id: label.id, label_number: label.number, farm_id: label.farm_id, grower_id: label.grower_id,
      ...payload, status: "active", gps_status: payload.gps ? "ok" : "exception_pending", gps_exception: payload.gps ? null : { reason: noGps },
      activated_at: now, activated_by_name: user.full_name, field_weight: null, photo: null, is_test: true, _local: true,
      names: { farm: nm(asg.farms, label.farm_id), sector: nm(sectors, f.sector_id), variety: nm(varieties, f.variety_id),
        contractor: nm(myContractors, f.contractor_id), crew: nm(myCrews, f.crew_id) },
      history: [{ id: newId(), action: "activated", by_name: user.full_name, at: now, offline: true }] });
    await enqueue({ type: "activate", bin_id: clientId, payload: { ...payload, client_created_at: now } });
    nav(`/bin/${clientId}`, { replace: true });
  };

  const submit = async (gps) => {
    setBusy(true); setErr("");
    const payload = { label_id: label.id, ...f, crew_id: f.crew_id || null, gps, no_gps_reason: gps ? null : noGps, client_id: clientId };
    localStorage.setItem("hf_last_activation", JSON.stringify({ contractor_id: f.contractor_id, crew_id: f.crew_id, sector_id: f.sector_id, variety_id: f.variety_id }));
    try {
      const { data } = await api.post("/bins/activate", payload);
      nav(`/bin/${data.id}`, { replace: true });
    } catch (e) {
      if (isNetErr(e)) return saveOffline(payload);
      const c = errCode(e);
      if (c.startsWith("already_activated:")) return nav(`/bin/${c.split(":")[1]}`, { replace: true });
      setErr(c);
    }
    setBusy(false);
  };
  const activate = async () => {
    setGpsState("capturing");
    try { const g = await getGPS(); setGpsState("ok"); await submit(g); }
    catch { setGpsState("failed"); }
  };

  return (
    <div className="max-w-md mx-auto space-y-4 pb-28" data-testid="activate-page">
      <div className="card p-4">
        <p className="eyebrow">{t("activate_bin")}</p>
        <p className="font-mono text-3xl font-bold" data-testid="activate-label-number">{label.number}</p>
      </div>
      {asg.last_activation && <p className="text-xs font-semibold text-emerald-800">{t("prefilled")}</p>}
      <div className="card p-4 space-y-4">
        <Sel label={t("sector")} value={f.sector_id} onChange={set("sector_id")} options={sectors} testid="activate-sector-select" placeholder={t("select")} />
        <Sel label={t("variety")} value={f.variety_id} onChange={set("variety_id")} options={varieties} testid="activate-variety-select" placeholder={t("select")} />
        <Inp label={t("harvest_date")} type="date" value={f.harvest_date} onChange={(e) => set("harvest_date")(e.target.value)} testid="activate-date-input" />
        {rel && (
          <div className={`p-3 rounded-xl border-2 ${released ? "border-emerald-300 bg-emerald-50" : "border-red-300 bg-red-50"}`} data-testid="release-status-box">
            <ReleaseBadge status={rel.status} />
            {rel.release?.reason && <p className="text-sm mt-1">{rel.release.reason}</p>}
            {!released && <p className="text-sm font-bold text-red-800 mt-1" data-testid="hold-blocked-message">{t("hold_blocked")}</p>}
          </div>
        )}
        <Sel label={t("contractor")} value={f.contractor_id} onChange={set("contractor_id")} options={myContractors} testid="activate-contractor-select" placeholder={t("select")} />
        <Sel label={`${t("crew")} (${t("optional")})`} value={f.crew_id} onChange={set("crew_id")} options={myCrews} testid="activate-crew-select" placeholder={t("select")} />
        <label className="block"><span className="eyebrow">{t("notes")}</span>
          <textarea className="field mt-1 py-2" rows={2} value={f.notes} onChange={(e) => set("notes")(e.target.value)} data-testid="activate-notes-input" /></label>
      </div>
      <p className="flex items-center gap-2 text-sm text-slate-600"><MapPin size={16} />{gpsState === "capturing" ? t("gps_capturing") : t("gps_note")}</p>
      {gpsState === "failed" && (
        <div className="card p-4 space-y-3 border-amber-400" data-testid="gps-failed-box">
          <p className="font-bold text-amber-900">{t("gps_failed")}</p>
          <Inp label={t("no_gps_reason")} value={noGps} onChange={(e) => setNoGps(e.target.value)} testid="no-gps-reason-input" />
          <div className="grid grid-cols-2 gap-2">
            <button className="btn-secondary" onClick={activate} data-testid="retry-gps-button">{t("retry_gps")}</button>
            <button className="btn-secondary" disabled={noGps.trim().length < 3 || !ok || busy} onClick={() => submit(null)} data-testid="activate-without-gps-button">{t("activate_without_gps")}</button>
          </div>
        </div>
      )}
      <ScanError err={err} />
      <div className="fixed bottom-0 inset-x-0 bg-white border-t border-slate-300 p-3 z-20">
        <button className="btn-primary w-full max-w-md mx-auto" disabled={!ok || busy || gpsState === "capturing"} onClick={activate} data-testid="activate-submit-button">
          <Zap size={20} />{t("activate")}
        </button>
      </div>
    </div>
  );
}
